<template>
    <div v-if="configLoaded && viewState.kind !== 'no-location'" class="zs-dock-row" :class="{ 'zs-dock-row--static': isStatic }" @click="onRowClick">
        <span class="zs-status-dot" :class="`zs-status-dot--${display.dot}`" />
        <div class="zs-dock-row__main">
            <div class="zs-dock-row__lbl">{{ display.label }}</div>
            <div v-if="display.sub" class="zs-dock-row__sub">{{ display.sub }}</div>
        </div>
        <span v-if="display.manual" class="zs-badge zs-badge--warn" v-tooltip="display.manual">Manual</span>
    </div>
</template>

<script lang="ts" setup>
import { OpenApiError, dataFrom } from '@zyno-io/openapi-client-codegen';
import { type ContextMenuItem, handleError, presentOverlay, showConfirm, showContextMenu } from '@zyno-io/vue-foundation';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type {
    LocationHoursPolicyResponse,
    LocationHoursStateResponse,
    LocationHoursStatus,
    LocationSummary
} from '@/openapi-clients-generated/suite';

import { GlobalState } from '@/global';
import { SSECoreLocationHoursChangedChannel } from '@/modules/core/sse';
import { SuiteCoreLocationHoursApi } from '@/openapi-clients-generated/suite';
import { DataCache } from '@/shared/data/cache';
import { hasPermission } from '@/shared/helpers/permissions.helpers';
import { withWait } from '@/shared/helpers/request.helpers';
import { SSEService } from '@/shared/services/sse';

import { computeNextRefreshDelayMs, dotToneForState, isConfiguredForDisplay, resolvedSubLine, type DotTone } from './location-status';

type ReadyState = { kind: 'ready'; location: LocationSummary; policy: LocationHoursPolicyResponse | null; state: LocationHoursStateResponse | null };
type ViewState = { kind: 'no-location' } | { kind: 'loading' } | { kind: 'error' } | ReadyState;

const isWorkstation = computed(() => GlobalState.isWorkstation);
// The dock only mounts this row once config has a location, but config can later reload without one
// (a `reconfigure`) before the dock unmounts it — render nothing in that window.
const configLoaded = computed(() => GlobalState.tenantConfiguration.value != null);
const locationId = computed(() => GlobalState.tenantConfiguration.value?.['workstation.locationId'] ?? null);
const canChange = computed(() => Boolean(GlobalState.tenant.value?.isAdmin) || hasPermission('core', 'changeLocationStatus'));

const viewState = ref<ViewState>({ kind: 'loading' });

const tzMissing = computed(() => {
    const state = viewState.value;
    return state.kind === 'ready' && state.location.tz == null;
});
// A location has a real status to show once its state row exists — even with no hours and no active
// override it's effectively closed, not "unconfigured". Only a missing resolved-state row has nothing
// to show — briefly possible right after deploy, before Suite lazily binds the location to a policy
// (see the `state` prop comment on m-manual-override.vue) — so `display` below can safely treat a
// present `state.state` as non-null once this is false. Timezone-missing takes priority over this
// (checked first in `display`), so it isn't re-checked here.
const hasNoState = computed(() => {
    const state = viewState.value;
    return state.kind === 'ready' && !isConfiguredForDisplay(state.state);
});
// Whether the schedule bound to this location has any hours at all — gates the manual-override
// modal's "next scheduled change" option the same way the admin screen does.
const scheduleConfigured = computed(() => {
    const state = viewState.value;
    return state.kind === 'ready' && Boolean(state.policy?.configured);
});

interface RowDisplay {
    label: string;
    sub?: string;
    dot: DotTone;
    manual?: string;
}

const display = computed<RowDisplay>(() => {
    const state = viewState.value;
    // 'no-location' (none assigned, or the location was deleted) isn't rendered at all.
    if (state.kind === 'no-location' || state.kind === 'loading') return { label: 'Loading…', dot: 'idle' };
    if (state.kind === 'error') return { label: 'Status unavailable', dot: 'idle' };

    const label = state.location.label;
    if (tzMissing.value) return { label, sub: 'Timezone not set', dot: 'idle' };
    if (hasNoState.value) return { label, sub: 'No schedule set', dot: 'idle' };

    const s = state.state!;
    return {
        label,
        sub: resolvedSubLine(s, state.policy?.configured, state.location.tz),
        dot: dotToneForState(s),
        manual: s.currentResolvedSource === 'manual_override' ? s.manualReason || 'Manual override' : undefined
    };
});

// Nothing to click while loading/absent-location/timezone-missing — those states have no menu to
// open. An error row is still clickable, but to retry the fetch rather than to open the menu.
const canOpenMenu = computed(() => viewState.value.kind === 'ready' && canChange.value && !tzMissing.value);
const isStatic = computed(() => viewState.value.kind !== 'error' && !canOpenMenu.value);

function isNotFound(err: unknown): boolean {
    return err instanceof OpenApiError && err.response?.status === 404;
}

// Sequence-token guard: a slow response for a location we've since navigated away from (or a
// superseded retry) must not clobber a newer one that already landed.
let seq = 0;
// Set on unmount so an in-flight load (or one awaited after a modal) can't re-arm timers.
let disposed = false;

async function load() {
    if (disposed) return;
    // Nothing to fetch yet, and the row isn't rendered — `configLoaded` flipping true re-triggers
    // the watcher below regardless of whether `locationId` itself also changed.
    if (!configLoaded.value) {
        seq++;
        return;
    }
    const id = locationId.value;
    if (!id) {
        seq++;
        viewState.value = { kind: 'no-location' };
        clearRefreshTimer();
        return;
    }
    const token = ++seq;
    if (viewState.value.kind !== 'ready') viewState.value = { kind: 'loading' };
    try {
        const response = dataFrom(await SuiteCoreLocationHoursApi.getCoreLocationHoursShow({ path: { locationId: id } }));
        if (token !== seq || disposed) return;
        viewState.value = { kind: 'ready', location: response.location, policy: response.policy, state: response.state };
        scheduleNextRefresh();
    } catch (err) {
        if (token !== seq) return;
        if (isNotFound(err)) {
            viewState.value = { kind: 'no-location' };
        } else {
            viewState.value = { kind: 'error' };
            handleError(err, 'Failed to load location status for the sidebar dock');
        }
    }
}

// ── Refresh scheduling ──────────────────────────────────────────────────────
// Three independent refresh sources, all converging on the same `load()`: an own-schedule timer
// landing just past the next known boundary, a 5-minute safety poll, and the location-hours SSE
// channel (coalesced — a schedule boundary can fan out several per-location events at once). The
// SSE-only event Suite emits when an override is set/cleared without an open/closed flip is what
// keeps the "Manual" badge live even though it carries no NATS/state-revision change.
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let pollTimer: ReturnType<typeof setInterval> | undefined;
let sseFlushTimer: ReturnType<typeof setTimeout> | undefined;
let sseDirty = false;

function clearRefreshTimer() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = undefined;
}

function scheduleNextRefresh() {
    clearRefreshTimer();
    if (disposed) return;
    const ready = viewState.value;
    if (ready.kind !== 'ready') return;
    const delay = computeNextRefreshDelayMs(ready.state);
    if (delay == null) return;
    refreshTimer = setTimeout(() => void load(), delay);
}

function scheduleSseFlush() {
    if (sseFlushTimer) return;
    sseFlushTimer = setTimeout(() => {
        sseFlushTimer = undefined;
        if (sseDirty) {
            sseDirty = false;
            void load();
        }
    }, 250);
}

SSECoreLocationHoursChangedChannel.use(event => {
    if (event.locationId !== locationId.value) return;
    sseDirty = true;
    scheduleSseFlush();
});

watch(SSEService.isConnected, (up, wasUp) => {
    if (up && wasUp === false) void load();
});

watch([isWorkstation, configLoaded, locationId], () => void load(), { immediate: true });

onMounted(() => {
    pollTimer = setInterval(() => void load(), 5 * 60_000);
});

onBeforeUnmount(() => {
    disposed = true;
    clearRefreshTimer();
    if (pollTimer) clearInterval(pollTimer);
    if (sseFlushTimer) clearTimeout(sseFlushTimer);
});

// ── Actions ──────────────────────────────────────────────────────────────────
function onRowClick(event: MouseEvent) {
    if (viewState.value.kind === 'error') {
        void load();
        return;
    }
    if (!canOpenMenu.value) return;
    openMenu(event);
}

function openMenu(event: MouseEvent) {
    const ready = viewState.value;
    if (ready.kind !== 'ready') return;
    const items: (ContextMenuItem | '-')[] = [
        { title: 'Open now…', handler: () => setManual('open') },
        { title: 'Close now…', handler: () => setManual('closed') }
    ];
    if (ready.state?.manualStatus) items.push('-', { title: 'Clear override', handler: () => clearOverride() });
    showContextMenu(event, { items });
}

// Guards re-entry across both actions below — a stray double-click while the modal/confirm is
// already open must not stack a second one.
const isBusy = ref(false);

async function setManual(defaultStatus: LocationHoursStatus) {
    const ready = viewState.value;
    if (isBusy.value || ready.kind !== 'ready') return;
    const { location, state } = ready;
    isBusy.value = true;
    try {
        // Lazy-imported: the manual-override modal belongs to the admin hours editor, which has no
        // business in the shell chunk this dock row ships in.
        const { default: MManualOverride } = await import('@/modules/admin/subapps/suite/screens/locations/hours/m-manual-override.vue');
        const result = await presentOverlay(MManualOverride, {
            locationId: location.id,
            locationLabel: location.label,
            tz: location.tz,
            state: state ?? undefined,
            scheduleConfigured: scheduleConfigured.value,
            defaultStatus
        });
        if (result) {
            DataCache.reloadCache('coreLocationHours', true);
            await load();
        }
    } finally {
        isBusy.value = false;
    }
}

async function clearOverride() {
    const ready = viewState.value;
    if (isBusy.value || ready.kind !== 'ready') return;
    const { location, state } = ready;
    const status = state?.manualStatus === 'open' ? 'open' : 'closed';
    const ok = await showConfirm('Clear manual override', `Remove the manual ${status} override for ${location.label}?`);
    if (!ok) return;
    isBusy.value = true;
    try {
        await withWait('Clearing…', async () => {
            await SuiteCoreLocationHoursApi.deleteCoreLocationHoursClearManual({ path: { locationId: location.id } });
        });
        DataCache.reloadCache('coreLocationHours', true);
        await load();
    } finally {
        isBusy.value = false;
    }
}
</script>
